import { ErrorBox, Loader } from "@tfg/ui"
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react"
import { useTranslation } from "react-i18next"

import type {
  SupplierOnboardingStep,
  SupplierOnboardingStepId,
} from "~/pages/SupplierPortal/Onboarding/constants/supplierOnboardingSteps"
import { useOnboardingStepper } from "~/pages/SupplierPortal/Onboarding/hooks/useOnboardingStepper"
import { useSupplierOnboardingSteps } from "~/pages/SupplierPortal/Onboarding/hooks/useSupplierOnboardingSteps"
import { useUserOnboardingState } from "~/pages/SupplierPortal/Onboarding/hooks/useUserOnboardingState"
import {
  getPersistedVerifyCompanyOutcome,
  getStubInvitedCompanySummary,
  persistVerifyCompanyOutcome,
} from "~/pages/SupplierPortal/Onboarding/verifyCompanyOutcome/verifyCompanyOutcome.stub"
import {
  DEFAULT_VERIFY_COMPANY_OUTCOME,
  type UseVerifyCompanyOutcomeStepResult,
  type VerifyCompanyOutcome,
} from "~/pages/SupplierPortal/Onboarding/verifyCompanyOutcome/verifyCompanyOutcome.types"

type OnboardingStatus = "not_started" | "in_progress" | "completed" | "blocked"

const resolveInitialVerifyCompanyOutcome = (): VerifyCompanyOutcome =>
  getPersistedVerifyCompanyOutcome() ?? DEFAULT_VERIFY_COMPANY_OUTCOME

export type OnboardingContextValue = Readonly<{
  status: OnboardingStatus
  steps: readonly SupplierOnboardingStep[]
  currentStep: SupplierOnboardingStep
  currentStepIndex: number
  currentStepId: SupplierOnboardingStepId
  isFirstStep: boolean
  isLastStep: boolean
  goToNextStep: () => void
  goToPreviousStep: () => void
  verifyCompanyOutcome: UseVerifyCompanyOutcomeStepResult
}>

const OnboardingContext = createContext<OnboardingContextValue | null>(null)

type OnboardingProviderProps = Readonly<{
  children: ReactNode
}>

export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const { t } = useTranslation()
  const steps = useSupplierOnboardingSteps()
  const onboardingState = useUserOnboardingState()
  const stepper = useOnboardingStepper({
    initialStepId: onboardingState.currentStepId,
    steps,
  })
  const invitedCompany = getStubInvitedCompanySummary()
  const [selectedOutcome, setSelectedOutcome] = useState<VerifyCompanyOutcome>(
    resolveInitialVerifyCompanyOutcome
  )

  const selectOutcome = useCallback((outcome: VerifyCompanyOutcome): void => {
    setSelectedOutcome(outcome)
  }, [])

  const handleVerifyCompanyContinue = useCallback((): boolean => {
    persistVerifyCompanyOutcome(selectedOutcome)
    return true
  }, [selectedOutcome])

  if (onboardingState.isLoading) {
    return <Loader fullPage />
  }

  if (onboardingState.isError) {
    return (
      <ErrorBox
        title={t("dataLoad.error.title")}
        error={onboardingState.error}
      />
    )
  }

  const value: OnboardingContextValue = {
    status: onboardingState.status,
    steps,
    currentStep: stepper.currentStep,
    currentStepIndex: stepper.currentStepIndex,
    currentStepId: stepper.currentStep.id,
    isFirstStep: stepper.isFirstStep,
    isLastStep: stepper.isLastStep,
    goToNextStep: stepper.goToNextStep,
    goToPreviousStep: stepper.goToPreviousStep,
    verifyCompanyOutcome: {
      invitedCompany,
      selectedOutcome,
      selectOutcome,
      handleContinue: handleVerifyCompanyContinue,
    },
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}

export const useOnboardingContext = (): OnboardingContextValue => {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error(
      "useOnboardingContext must be used within an OnboardingProvider"
    )
  }
  return context
}
